import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { updateSelectedConversationId } from "./DbtDocsReducer";
import { getConversationGroupTargetElement } from "./highlightUtils";
import { ConversationGroup } from "./conversations/context/types";
import {
  useDbtDocsCollaborationDispatch,
  useDbtDocsCollaborationSelector,
} from "./DbtDocsCollaborationProvider";
import { CommentUnresolvedIcon } from "../../../assets/icons";

const ConversationHighlighter = ({
  conversationGroup,
}: {
  conversationGroup: ConversationGroup;
}) => {
  const selectedConversationId = useDbtDocsCollaborationSelector(
    (state) => state.selectedConversationId,
  ) as ConversationGroup["conversation_group_id"] | undefined;
  const dispatch = useDbtDocsCollaborationDispatch();
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const target = useMemo(() => {
    return getConversationGroupTargetElement(conversationGroup.meta);
  }, [conversationGroup.meta]);

  const handleClick = () => {
    dispatch(
      updateSelectedConversationId(conversationGroup.conversation_group_id),
    );
  };

  const position = useMemo(() => {
    if (!target) {
      return;
    }

    if (conversationGroup.meta.field === "description") {
      return { top: 0, bottom: target.offsetHeight };
    }

    let top = 0;
    let bottom = 0;
    for (
      let i = conversationGroup.meta.range.start.line;
      i <= conversationGroup.meta.range.end.line;
      i++
    ) {
      const lineNumber = target.querySelector(
        `.line-numbers-rows > span:nth-child(${i + 1})`,
      );
      if (lineNumber) {
        if (i === conversationGroup.meta.range.start.line) {
          top = (lineNumber as HTMLSpanElement).offsetTop + 15;
        }
        if (i === conversationGroup.meta.range.end.line) {
          bottom =
            (lineNumber as HTMLSpanElement).offsetTop +
            (lineNumber as HTMLSpanElement).offsetHeight;
        }
        // (lineNumber as HTMLSpanElement).classList.add("altimate-highlight");
      }
    }
    return { top, bottom };
  }, [
    target,
    conversationGroup.meta.field,
    conversationGroup.meta.range.start.line,
    conversationGroup.meta.range.end.line,
  ]);

  useEffect(() => {
    if (selectedConversationId !== conversationGroup.conversation_group_id) {
      return;
    }

    highlightRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [conversationGroup.conversation_group_id, selectedConversationId]);

  if (!position || !target?.parentElement) {
    return null;
  }

  return createPortal(
    <div
      ref={highlightRef}
      className={`altimate-highlighter ${selectedConversationId === conversationGroup.conversation_group_id ? "active" : ""}`}
      style={{ top: position.top, height: position.bottom - position.top }}
      onClick={handleClick}
    >
      <CommentUnresolvedIcon />
    </div>,
    target.parentElement,
  );
};

export default ConversationHighlighter;
